import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { NetworkSpinnerWrapper } from 'ui/components/NetworkSpinnerWrapper';
import { Auth0WrapperProps } from './type';
import { useDispatch, useSelector } from 'react-redux';
import { addAuth0Interceptors } from '../../../../../helpers/axiosInterceptors';
import { fetchActiveUser, getActiveUser } from '../../../../../services/userV2';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchQuickbooksActiveAccount } from 'services/integrations/quickbooks/redux';
import { fetchShippingIntegrationConnections } from 'services/integrations/shipping/redux';
import { fetchSettingsCompanies } from 'services/settings/company';
import { fetchPaymentTerms } from 'services/paymentTerms';
import { fetchXeroActiveAccount } from 'services/integrations/xero';
import { fetchSellwareActiveAccount } from 'services/commerce/redux';
import { Routes } from 'ui/modules/public/navigation';
import { fetchInsightsEntitlement } from 'services/insights/redux';

const Auth0Handler: React.FC<Auth0WrapperProps> = ({ children }) => {
  const auth0 = useAuth0();
  const {
    loginWithRedirect,
    isLoading,
    getAccessTokenSilently,
    handleRedirectCallback,
    logout,
    user: auth0User,
  } = auth0;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [accessTokenLoaded, setAccessTokenLoaded] = useState(false);
  const [callbackComplete, setCallbackComplete] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const { user, companyExpirationDate } = useSelector(getActiveUser);

  useEffect(() => {
    (async () => {
      try {
        if (params.has('error')) {
          await logout({
            logoutParams: {
              returnTo: process.env.REACT_APP_AUTH0_CALLBACK_URL,
            },
          });
        } else if (params.has('code')) {
          console.log('[Auth0Handler] Processing Auth0 callback with code');

          // Extract appState from Auth0 callback (contains returnTo path)
          const callbackResult = await handleRedirectCallback(
            window.location.href
          );
          console.log('[Auth0Handler] Redirect callback complete');

          const accessToken = await getAccessTokenSilently();
          console.log('[Auth0Handler] Access token obtained');

          addAuth0Interceptors(accessToken, logout);
          dispatch(fetchActiveUser() as any);
          // @ts-ignore
          dispatch(fetchQuickbooksActiveAccount());
          // @ts-ignore
          dispatch(fetchShippingIntegrationConnections());
          // @ts-ignore
          dispatch(fetchSettingsCompanies());
          // @ts-ignore
          dispatch(fetchPaymentTerms());
          // @ts-ignore
          dispatch(fetchXeroActiveAccount());
          // @ts-ignore
          dispatch(fetchSellwareActiveAccount());
          // @ts-ignore
          dispatch(fetchInsightsEntitlement());
          // set the access token
          setAccessTokenLoaded(true);

          // Set flag to trigger MFE routing check on next render when auth0User populates
          console.log(
            '[Auth0Handler] Callback complete, setting flag for MFE routing check'
          );
          setCallbackComplete(true);

          // Restore path from Auth0 appState (cross-origin safe)
          const returnTo = callbackResult?.appState?.returnTo;
          const redirectTo = returnTo || sessionStorage.getItem('redirectTo'); // Fallback to old method

          if (redirectTo && redirectTo !== '/logout') {
            console.log(
              '[Auth0Handler] Restoring path from appState:',
              redirectTo
            );
            navigate(redirectTo);
          }
          // temporary redirect for users that get to old registration endpoint
        } else if (
          location.pathname === '/create-account' &&
          process.env.REACT_APP_NEW_REGISTRATION_URL
        ) {
          window.location.href = `${process.env.REACT_APP_NEW_REGISTRATION_URL}?app=drive`;
        } else if (params.has('invitation')) {
          await loginWithRedirect({
            authorizationParams: {
              organization: params.get('organization') ?? '',
              invitation: params.get('invitation') ?? '',
            },
          });
        } else {
          const fullURL = `${location.pathname}${location.search}${location.hash}`;
          sessionStorage.setItem('redirectTo', fullURL);
          loginWithRedirect({
            authorizationParams: {
              redirect_uri: process.env.REACT_APP_AUTH0_CALLBACK_URL,
            },
          }).then();
        }
      } catch (error) {
        console.error('Error in Auth0Handler', error);
        navigate('/logout');
      }
    })();
  }, []);

  // Separate useEffect to check MFE routing after auth0User populates
  useEffect(() => {
    (async () => {
      if (callbackComplete && auth0User) {
        console.log(
          '[Auth0Handler] auth0User populated after callback, checking MFE routing'
        );
        console.log('[Auth0Handler] auth0User:', {
          sub: auth0User.sub,
          email: auth0User.email,
          hasOrgId:
            !!(auth0User as any).org_id || !!(auth0User as any).organizationId,
          hasRoles: !!(auth0User as any).roles,
          roles: (auth0User as any).roles,
        });

        try {
          const { checkMfeRoutingAfterAuth } = await import(
            '../../../../../mfeRouting'
          );
          const shouldRedirectToMFE = await checkMfeRoutingAfterAuth(auth0User);
          if (shouldRedirectToMFE) {
            console.log('[Auth0Handler] MFE routing triggered redirect');
            // Redirect happening, component will unmount
            return;
          }
          console.log('[Auth0Handler] MFE routing complete, staying on legacy');
        } catch (error) {
          console.error('[Auth0Handler] MFE routing check failed', error);
        }
      }
    })();
  }, [callbackComplete, auth0User]);

  useEffect(() => {
    if (
      companyExpirationDate &&
      new Date(companyExpirationDate) < new Date() &&
      location.pathname !== Routes.AccountStatusPage
    ) {
      navigate(Routes.AccountStatusPage);
    }
  }, [companyExpirationDate, location.pathname, history]);

  if (isLoading || !accessTokenLoaded || !user) {
    return (
      <NetworkSpinnerWrapper
        isLoading={isLoading}
        display="flex"
        flexDirection="column"
        overflow="hidden"
        flexGrow={1}
        zIndex={2}
      >
        {/* Empty fragment used as a placeholder for required children props */}
        <></>
      </NetworkSpinnerWrapper>
    );
  }

  return <>{children}</>;
};

export default Auth0Handler;
